import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import ArrowBack from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

import { getPqrsdStats } from "../../services/backend/PqrsdService";
import { PQRSD_TYPES, PQRSD_PRIORITY_LABELS } from "../../constants";
import { StatCard, ChartCard } from "../generic/ReportCards";
import PqrsdReportBuilder from "./PqrsdReportBuilder";

import { pqrsdText } from "./pqrsdLabel";
// Validated categorical palette (data-viz reference instance). Assigned in
// fixed order — never cycled. Kept small; charts here never need > 4 series.
const SERIES = ["#2a78d6", "#1baf7a", "#eda100", "#4a3aa7", "#e34948", "#eb6834"];
// Reserved status palette (good / warning / serious / critical) — used only for
// the KPI semantic coloring, never as a chart series color.
const STATUS = { good: "#0ca30c", warning: "#fab219", serious: "#ec835a", critical: "#d03b3b" };

const STATUS_LABELS = {
  radicado: "Radicado",
  asignado: "Asignado",
  en_tramite: "En trámite",
  respondido: "Respondido",
  cerrado: "Cerrado",
  devuelto: "Devuelto",
  reabierto: "Reabierto",
  pendiente_info: "Pendiente de información",
  desistido: "Desistida",
  en_recurso: "En recurso de reposición",
};
const CHANNEL_LABELS = {
  web: "Web",
  ventanilla: "Ventanilla",
  email: "Correo",
  correo: "Correo",
};

// Turns a { key: count } breakdown into sorted, labelled arrays for a bar chart.
const toBars = (obj = {}, labelMap) => {
  const entries = Object.entries(obj).sort((a, b) => b[1] - a[1]);
  return {
    labels: entries.map(([k]) => (labelMap && labelMap[k]) || k),
    values: entries.map(([, v]) => v),
  };
};

// Left margin that fits the longest band label at the axis' 12px font
// (~6.6px per character), within sane bounds.
const barLabelMargin = (labels) =>
  Math.min(280, Math.max(80, Math.ceil(Math.max(0, ...labels.map((l) => String(l).length)) * 6.6) + 16));

const fmtInt = (n) => new Intl.NumberFormat("es-CO").format(n || 0);
// 3.8 → "3,8", the decimal comma the rest of the page uses.
const fmtScore = (n) => new Intl.NumberFormat("es-CO", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
// A percentage the backend could not compute (nothing to divide by) is null:
// show a dash rather than a 0% that reads as a failure.
const fmtPct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);

// Preset ranges. Values are moment objects (same convention as Reports.js).
const PRESETS = [
  { key: "month", label: "Este mes", from: () => moment().startOf("month"), to: () => moment().endOf("day") },
  { key: "30d", label: "Últimos 30 días", from: () => moment().subtract(29, "days").startOf("day"), to: () => moment().endOf("day") },
  { key: "year", label: "Este año", from: () => moment().startOf("year"), to: () => moment().endOf("day") },
  { key: "all", label: "Todo", from: () => null, to: () => null },
];

export default function PqrsdReports() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [preset, setPreset] = useState("30d");
  const [from, setFrom] = useState(PRESETS.find((p) => p.key === "30d").from());
  const [to, setTo] = useState(PRESETS.find((p) => p.key === "30d").to());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [exportError, setExportError] = useState(false);
  // "tablero" (charts) | "informes" (Excel reports); both use the date range.
  const [view, setView] = useState("tablero");

  const load = useCallback(async (f, t) => {
    setLoading(true);
    setError(false);
    // moment objects mutate in place: widen copies, not the pickers' values.
    const body = await getPqrsdStats({
      from: f ? f.clone().startOf("day").format() : null,
      to: t ? t.clone().endOf("day").format() : null,
    });
    if (!body) setError(true);
    setStats(body);
    setLoading(false);
  }, []);

  useEffect(() => {
    load(from, to);
    // Initial load only; subsequent loads are triggered explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    setPreset(p.key);
    const f = p.from();
    const t = p.to();
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const applyCustom = (f, t) => {
    setPreset("custom");
    load(f, t);
  };

  const axisSx = useMemo(
    () => ({
      "& .MuiChartsAxis-tickLabel": { fill: theme.palette.text.secondary },
      "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: theme.palette.divider },
    }),
    [theme]
  );

  const totals = stats?.totals;
  const ai = stats?.ai;
  const dev = stats?.devoluciones;
  // Older pqrsdStats deployments do not send it: the card shows a dash.
  const sat = stats?.satisfaction;

  // Every exported PNG states the period it was computed for, taken from the
  // range the backend answered (not the pickers, which may have moved since).
  const exportInfo = useMemo(() => {
    const range = stats?.range;
    const f = range?.from ? moment(range.from) : null;
    const t = range?.to ? moment(range.to) : null;
    let subtitle = "Período: todo el historial";
    if (f && t) subtitle = `Período: ${f.format("DD/MM/YYYY")} – ${t.format("DD/MM/YYYY")}`;
    else if (f) subtitle = `Período: desde ${f.format("DD/MM/YYYY")}`;
    else if (t) subtitle = `Período: hasta ${t.format("DD/MM/YYYY")}`;
    const rangeKey = f || t
      ? `${f ? f.format("YYYYMMDD") : "inicio"}_${t ? t.format("YYYYMMDD") : "hoy"}`
      : "todo";
    return { subtitle, rangeKey, onError: () => setExportError(true) };
  }, [stats]);

  const aiColor =
    !ai || ai.accuracyPct == null
      ? "text.primary"
      : ai.accuracyPct >= 85
      ? STATUS.good
      : ai.accuracyPct >= 70
      ? STATUS.warning
      : STATUS.critical;
  const slaColor =
    !totals || totals.slaCompliantPct == null
      ? "text.primary"
      : totals.slaCompliantPct >= 90
      ? STATUS.good
      : totals.slaCompliantPct >= 75
      ? STATUS.warning
      : STATUS.critical;

  const satColor =
    !sat || sat.avgScore == null
      ? "text.primary"
      : sat.avgScore >= 4
      ? STATUS.good
      : sat.avgScore >= 3
      ? STATUS.warning
      : STATUS.critical;

  // Chart-ready derivations.
  const statusBars = useMemo(() => toBars(totals?.byStatus, STATUS_LABELS), [totals]);
  const typeBars = useMemo(() => toBars(stats?.breakdowns?.byType, PQRSD_TYPES), [stats]);
  const groupBars = useMemo(() => toBars(stats?.breakdowns?.byGroup), [stats]);
  const channelBars = useMemo(() => toBars(stats?.breakdowns?.byChannel, CHANNEL_LABELS), [stats]);
  const priorityBars = useMemo(() => toBars(stats?.breakdowns?.byPriority, PQRSD_PRIORITY_LABELS), [stats]);
  // Kept in the backend's order (best score first), not re-sorted by count.
  const ratingBars = useMemo(() => {
    const list = sat?.byOption || [];
    return { labels: list.map((o) => `${o.label} (${o.score})`), values: list.map((o) => o.count) };
  }, [sat]);
  const trend = stats?.trend || [];

  const aiPie = useMemo(() => {
    if (!ai) return [];
    return [
      { id: 0, value: ai.correct, label: "Enrutó bien", color: STATUS.good },
      { id: 1, value: ai.corrected, label: "Enrutamiento corregido", color: STATUS.serious },
      { id: 2, value: ai.needsReview, label: "Revisión (Triage)", color: STATUS.warning },
      { id: 3, value: ai.failed, label: "Falló", color: STATUS.critical },
    ].filter((d) => d.value > 0);
  }, [ai]);

  const HBar = ({ bars }) => (
    <BarChart
      layout="horizontal"
      height={Math.max(200, bars.labels.length * 44 + 60)}
      yAxis={[{ scaleType: "band", data: bars.labels }]}
      // Counts of PQRSD: whole-number ticks, never 0.05 or 0.5.
      // The series is bound to it by id: an unbound axis is drawn while the bars
      // keep measuring against the hidden default one.
      xAxis={[{ id: "count", tickMinStep: 1 }]}
      series={[{ data: bars.values, color: SERIES[0], xAxisKey: "count" }]}
      // Room for the longest label ("Solicitud de información o documentación"
      // is ~40 characters) so it is not clipped on screen or in the PNG.
      margin={{ left: barLabelMargin(bars.labels), right: 24, top: 10, bottom: 30 }}
      sx={axisSx}
      slotProps={{ legend: { hidden: true } }}
    />
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Volver
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {pqrsdText("Informes PQRSD")}
        </Typography>
        {view === "tablero" && (
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => load(from, to)}
            disabled={loading}
          >
            Actualizar
          </Button>
        )}
      </Stack>

      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="tablero" label="Tablero" />
        <Tab value="informes" label="Informes (Excel)" />
      </Tabs>

      {/* Date-range controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {PRESETS.map((p) => (
              <Chip
                key={p.key}
                label={p.label}
                color={preset === p.key ? "primary" : "default"}
                variant={preset === p.key ? "filled" : "outlined"}
                onClick={() => applyPreset(p)}
              />
            ))}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <DatePicker
              label="Desde"
              value={from}
              slotProps={{ textField: { size: "small" } }}
              onChange={(m) => {
                setFrom(m);
                applyCustom(m, to);
              }}
            />
            <DatePicker
              label="Hasta"
              value={to}
              slotProps={{ textField: { size: "small" } }}
              onChange={(m) => {
                setTo(m);
                applyCustom(from, m);
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {view === "informes" && <PqrsdReportBuilder from={from} to={to} />}

      {view === "tablero" && (
        <>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography color="error" gutterBottom>
            No fue posible cargar las estadísticas.
          </Typography>
          <Button variant="contained" onClick={() => load(from, to)}>
            Reintentar
          </Button>
        </Paper>
      )}

      {!loading && !error && stats && (
        <>
          {/* KPI row */}
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} md={2}>
              <StatCard title="Recibidos" value={fmtInt(totals.received)} subtitle="en el período" />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                title="Atendidos"
                value={fmtInt(totals.attended)}
                subtitle={`${fmtPct(totals.attendedPct)} del total`}
                color={STATUS.good}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                title="Precisión IA"
                value={fmtPct(ai.accuracyPct)}
                subtitle={`${fmtInt(ai.correct)} de ${fmtInt(ai.ran)} clasificadas`}
                color={aiColor}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                title="Cumplimiento SLA"
                value={fmtPct(totals.slaCompliantPct)}
                subtitle={`${fmtInt(totals.slaCompliant)} de ${fmtInt(totals.slaEvaluated)} a tiempo · ${fmtInt(totals.overdue)} vencidas`}
                color={slaColor}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                title="Devoluciones"
                value={fmtInt(dev.count)}
                subtitle={pqrsdText(`${fmtPct(dev.rate)} de las PQRSD`)}
                color={dev.count > 0 ? STATUS.serious : "text.primary"}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                title="Satisfacción"
                value={sat?.avgScore == null ? "—" : `${fmtScore(sat.avgScore)} / 5`}
                subtitle={sat?.rated
                  ? `${fmtInt(sat.rated)} de ${fmtInt(sat.ratable)} calificadas (${fmtPct(sat.ratedPct)})`
                  : "ninguna calificada en el período"}
                color={satColor}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Trends */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <ChartCard title="Recibidos por mes y cuántos ya se atendieron" empty={trend.length === 0} exportInfo={exportInfo}>
                <LineChart
                  height={280}
                  xAxis={[{ scaleType: "point", data: trend.map((t) => t.period) }]}
                  series={[
                    { data: trend.map((t) => t.received), label: "Recibidos", color: SERIES[0], curve: "linear" },
                    { data: trend.map((t) => t.attended), label: "Atendidos", color: SERIES[1], curve: "linear" },
                  ]}
                  sx={axisSx}
                />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title="Precisión de la IA por mes (%)" empty={trend.every((t) => t.aiAccuracyPct == null)} exportInfo={exportInfo}>
                <LineChart
                  height={280}
                  xAxis={[{ scaleType: "point", data: trend.map((t) => t.period) }]}
                  yAxis={[{ min: 0, max: 100 }]}
                  series={[
                    { data: trend.map((t) => t.aiAccuracyPct), label: "Precisión IA", color: SERIES[3], curve: "linear" },
                  ]}
                  sx={axisSx}
                />
              </ChartCard>
            </Grid>
          </Grid>

          {/* Status + AI composition */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={7}>
              <ChartCard title="Distribución por estado" empty={statusBars.values.every((v) => v === 0)} exportInfo={exportInfo}>
                <HBar bars={statusBars} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={5}>
              <ChartCard title="Resultado de la clasificación IA" empty={aiPie.length === 0} exportInfo={exportInfo}>
                <PieChart
                  height={260}
                  series={[
                    {
                      data: aiPie,
                      innerRadius: 55,
                      paddingAngle: 2,
                      cornerRadius: 4,
                    },
                  ]}
                  // Keep the donut out of the legend's column: left to itself it
                  // sizes to the whole card and paints over the swatches.
                  margin={{ top: 10, bottom: 10, left: 10, right: 190 }}
                  slotProps={{
                    legend: {
                      direction: "column",
                      position: { vertical: "middle", horizontal: "right" },
                      padding: 0,
                    },
                  }}
                  sx={axisSx}
                />
              </ChartCard>
            </Grid>
          </Grid>

          {/* Breakdowns */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <ChartCard
                title="Calificación del ciudadano (encuesta de satisfacción)"
                empty={ratingBars.labels.length === 0}
                exportInfo={exportInfo}
              >
                <HBar bars={ratingBars} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title="Por tipo" empty={typeBars.labels.length === 0} exportInfo={exportInfo}>
                <HBar bars={typeBars} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title="Por dependencia" empty={groupBars.labels.length === 0} exportInfo={exportInfo}>
                <HBar bars={groupBars} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title="Por canal" empty={channelBars.labels.length === 0} exportInfo={exportInfo}>
                <HBar bars={channelBars} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title="Por prioridad" empty={priorityBars.labels.length === 0} exportInfo={exportInfo}>
                <HBar bars={priorityBars} />
              </ChartCard>
            </Grid>
          </Grid>

          {/* Devoluciones detail */}
          <ChartCard title="Devoluciones" empty={false}>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6} sm={3}>
                <StatCard title="Total" value={fmtInt(dev.count)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard title="Tasa" value={fmtPct(dev.rate)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard title="Devoluciones prom." value={(dev.avgReturnCount ?? 0).toFixed(2)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard title="IA corregida" value={fmtInt(ai.corrected)} subtitle="enrutamientos deshechos" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Motivos más frecuentes
                </Typography>
                {dev.topReasons.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin devoluciones en el período.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {dev.topReasons.map((r, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={r.count} />
                        <Typography variant="body2">{r.reason}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </ChartCard>
        </>
      )}
      <Snackbar
        open={exportError}
        autoHideDuration={5000}
        onClose={() => setExportError(false)}
        message="No fue posible descargar el gráfico."
      />
        </>
      )}
    </Container>
  );
}
